import { TransformBuffer, type BodyHandle, type ContactHitEvent } from 'box3d-web';
import type { FractureCell, Vec3 } from '../../services/fracture/voronoiFracture';
import { assertSleepAllowed } from '../../services/invariantMonitor';
import { applyImpulseToBody } from './fractureOrchestrator';
import { appendDemolitionEvent } from './events';
import { addOwnedSphere, createOwnedBody, destroyOwnedBody } from './nativeOwnership';
import { setLiveCells, spawnCellBody, worldCentroid } from './wallSetup';
import {
  CANNONBALL_DENSITY,
  CANNONBALL_PHYSICS_DENSITY,
  CANNONBALL_RADIUS,
  CANNONBALL_SPEED,
  CHARGE_DENSITY,
  CHARGE_RADIUS,
  CHARGE_SPEED,
  CONCUSSION_DENSITY,
  CONCUSSION_RADIUS,
  CONCUSSION_SPEED,
  DEMO_FIXED_DT,
  FADE_DURATION,
  GROUND_EPS,
  LAUNCH_CLAMP_WINDOW_SEC,
  MAX_DYNAMIC_CHUNKS,
  MAX_LAUNCH_SPEED,
  PROJECTILE_MAX_RECOVERIES,
  PROJECTILE_REST_DWELL_SEC,
  PROJECTILE_REST_RESET_SPEED,
  PROJECTILE_STALL_RECOVERY_COOLDOWN,
  PROJECTILE_STALL_SPEED_EPS,
  PROJECTILE_WALL_CAGE_MARGIN,
  RUBBLE_CONTACT_RESTITUTION,
  RUBBLE_DENSITY,
  RUBBLE_RADIUS,
  RUBBLE_SPEED,
  SLEEP_MOVE_EPS,
  SLEEP_SLOW_STREAK,
  SLEEP_SWEEP_INTERVAL,
  SLEEP_VEL_EPS,
  SPIKE_DENSITY,
  SPIKE_RADIUS,
  SPIKE_SPEED,
  SPIKE_UNEMBED_RADIUS,
  clampWeaponPower,
} from './demolitionTuning';
import type {
  CellRecord,
  DemoTool,
  DemolitionImpact,
  DemolitionNativeParticipant,
  DemolitionRuntime,
} from './types';

export function deterministicShardScale(cellId: number, impact: DemolitionImpact, wallVersion: number): number {
  let h = Math.imul(cellId | 0, 0x9e3779b1) ^ Math.imul(wallVersion | 0, 0x85ebca6b);
  h ^= Math.imul(Math.floor(impact.x * 1000) | 0, 0x27d4eb2d);
  h ^= Math.imul(Math.floor(impact.y * 1000) | 0, 0x165667b1);
  h ^= Math.imul(Math.floor(impact.z * 1000) | 0, 0xd3a2646c);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  return 0.7 + ((h ^ (h >>> 16)) >>> 0) / 0x1_0000_0000 * 0.5;
}

export function projectileSpecMass(weapon: DemoTool): { radius: number; mass: number } {
  const spec = PROJECTILE_SPECS[weapon] ?? PROJECTILE_SPECS.cannon;
  const mass = spec.density * ((4 / 3) * Math.PI * spec.radius ** 3);
  return { radius: spec.radius, mass };
}

/** Mass used only by the calibrated damage model and impact diagnostics. */
export function projectileDamageMass(weapon: DemoTool): { radius: number; mass: number } {
  const spec = PROJECTILE_SPECS[weapon] ?? PROJECTILE_SPECS.cannon;
  const density = spec.damageDensity ?? spec.density;
  const mass = density * ((4 / 3) * Math.PI * spec.radius ** 3);
  return { radius: spec.radius, mass };
}

/** Count current dynamic physics bodies (state === 'dynamic'). */
export function countDynamics(rt: DemolitionRuntime): number {
  let n = 0;
  for (const rec of rt.records.values()) {
    if (rec.state === 'dynamic') n += 1;
  }
  return n;
}

export function readBodySpeed(rt: DemolitionRuntime, bodyId: BodyHandle): {
  x: number;
  y: number;
  z: number;
  speed: number;
} | null {
  try {
    const v = rt.world.getLinearVelocity(bodyId);
    const x = Number(v.x);
    const y = Number(v.y);
    const z = Number(v.z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
    return { x, y, z, speed: Math.hypot(x, y, z) };
  } catch {
    return null;
  }
}

/**
 * Hard clamp linear velocity after blast / kinetic carve impulses.
 * Scales down; never zeros.
 */
export function clampLaunchSpeeds(
  rt: DemolitionRuntime,
  bodyIds: readonly BodyHandle[],
  maxSpeed: number = MAX_LAUNCH_SPEED,
) {
  if (bodyIds.length === 0) return;
  for (const bodyId of bodyIds) {
    const v = readBodySpeed(rt, bodyId);
    if (!v || !(v.speed > maxSpeed)) continue;
    const s = maxSpeed / v.speed;
    try {
      rt.world.setLinearVelocity(bodyId, [v.x * s, v.y * s, v.z * s]);
      // Wake so the engine commits the new velocity on sleeping islands.
      try {
        rt.world.wakeBody(bodyId);
      } catch {
        // ignore
      }
    } catch {
      // ignore
    }
  }
}

/**
 * Clamp all live dynamic chunks + runaway cannonballs.
 * Post-contact resolution / CCD can re-inject Δv past the one-shot post-impulse clamp.
 */
export function clampAllDynamicLaunches(rt: DemolitionRuntime) {
  // Always clamp dynamics while any activity window is open OR any overspeed exists.
  const inWindow =
    rt.lastActivitySimTime > 0 &&
    rt.stepper.simTime - rt.lastActivitySimTime <= LAUNCH_CLAMP_WINDOW_SEC;
  const bodies: BodyHandle[] = [];
  for (const rec of rt.records.values()) {
    if (rec.state === 'dynamic' && rec.bodyId) bodies.push(rec.bodyId as BodyHandle);
  }
  if (inWindow || bodies.length > 0) {
    clampLaunchSpeeds(rt, bodies, MAX_LAUNCH_SPEED);
  }
  // Cannonballs: muzzle is 55; allow brief overshoot from impact, hard-cap below
  // the monitor ceiling (82) so float residue never trips speed-ceiling.
  if (rt.cannonballs.length > 0) {
    clampLaunchSpeeds(rt, rt.cannonballs as BodyHandle[], 75);
  }
}

/**
 * Spawn a dynamic body, or degrade to a ballistic shard if the dyn cap would
 * be exceeded. Returns the body handle, or null if sharded/skipped.
 * `removedChunks` increments on shard-degrade (counts toward bookkeeping identity).
 */
export function spawnDynamicRespectingCap(
  rt: DemolitionRuntime,
  cell: FractureCell,
  opts: {
    isSubChunk?: boolean;
    preCrackGen?: number;
    impact: DemolitionImpact;
    radius: number;
    strength: number;
  },
): BodyHandle | null {
  if (countDynamics(rt) >= MAX_DYNAMIC_CHUNKS) {
    // F3: degrade smallest would-be chunks to shard particles (no physics body).
    queueShardFromCell(rt, cell, opts.impact, opts.radius, opts.strength);
    rt.removedChunks += 1;
    // Drop from live registry so maxLiveCells / bookkeeping stay coherent.
    setLiveCells(rt, rt.cells.filter((c) => c.id !== cell.id));
    return null;
  }
  const rec = spawnCellBody(rt, cell, 'dynamic', {
    isSubChunk: opts.isSubChunk,
    preCrackGen: opts.preCrackGen,
  });
  return rec.bodyId as BodyHandle;
}

/** Approximate radial-impulse launch velocity for ballistic shards. */
export function shardLaunchVelocity(
  rt: DemolitionRuntime,
  worldPos: Vec3,
  impact: DemolitionImpact,
  radius: number,
  strength: number,
  mass: number,
): Vec3 {
  const dx = worldPos.x - impact.x;
  const dy = worldPos.y - impact.y;
  const dz = worldPos.z - impact.z;
  const d = Math.hypot(dx, dy, dz) || 1e-6;
  if (d > radius) {
    return { x: 0, y: 0.5, z: 0 };
  }
  const t = 1 - d / radius;
  const falloff = t * t;
  const mag = (strength / Math.max(mass, 0.05)) * falloff;
  const inv = 1 / d;
  const up = rt.profile.impulse.upwardBias;
  return {
    x: dx * inv * mag,
    y: dy * inv * mag + up * mag,
    z: dz * inv * mag,
  };
}

export function queueShardFromCell(
  rt: DemolitionRuntime,
  cell: FractureCell,
  impact: DemolitionImpact,
  radius: number,
  strength: number,
) {
  const wc = worldCentroid(rt, cell);
  const mass = Math.max(rt.profile.density * cell.volume, 0.05);
  const vel = shardLaunchVelocity(rt, wc, impact, radius, strength, mass);
  appendDemolitionEvent(rt, 'shard', {
    x: wc.x,
    y: wc.y,
    z: wc.z,
    vx: vel.x,
    vy: vel.y,
    vz: vel.z,
    volume: cell.volume,
    material: rt.profile.id,
  });
}

export function queueShardFromCellVel(
  rt: DemolitionRuntime,
  cell: FractureCell,
  impact: DemolitionImpact,
  impulseVel: { vx: number; vy: number; vz: number } | null,
) {
  const wc = worldCentroid(rt, cell);
  const scale = deterministicShardScale(cell.id, impact, rt.wallVersion);
  const vx = impulseVel ? impulseVel.vx * scale : (wc.x - impact.x) * 2;
  const vy = impulseVel ? impulseVel.vy * scale + 0.5 : 1.5;
  const vz = impulseVel ? impulseVel.vz * scale : (wc.z - impact.z) * 2;
  appendDemolitionEvent(rt, 'shard', {
    x: wc.x,
    y: wc.y,
    z: wc.z,
    vx,
    vy,
    vz,
    volume: cell.volume,
    material: rt.profile.id,
  });
}

// ---------------------------------------------------------------------------
// Rubble lifecycle
// ---------------------------------------------------------------------------

export function cullOldRubble(rt: DemolitionRuntime, dt: number) {
  // Fade out already-marked removals.
  for (const rec of rt.records.values()) {
    if (rec.state === 'removed' && rec.fade >= 0) {
      rec.fade += dt / FADE_DURATION;
      if (rec.fade >= 1 && rec.bodyId) {
        try {
          destroyOwnedBody(rt, rec.bodyId as BodyHandle);
        } catch {
          // ignore
        }
        rec.bodyId = 0;
        rt.idsDirty = true;
      }
    }
  }

  if (rt.dynamicChunks <= MAX_DYNAMIC_CHUNKS) return;

  // Collect dynamic chunks that are asleep, oldest first.
  const candidates: CellRecord[] = [];
  for (const rec of rt.records.values()) {
    if (rec.state !== 'dynamic') continue;
    const idx = rt.bodyIndex.get(rec.bodyId);
    if (idx == null) continue;
    // Prefer sleep state if available.
    let asleep = false;
    if (rt.cellSleepStates.length > 0) {
      // Find in cellBodyIds
      const cellIdx = rt.cellBodyIds.indexOf(rec.bodyId);
      if (cellIdx >= 0 && rt.cellSleepStates[cellIdx] === 0) asleep = true;
    } else if (rt.prevValid && idx != null) {
      const o = idx * 7;
      const dx = Math.abs(rt.transforms[o] - rt.prevTransforms[o]);
      const dy = Math.abs(rt.transforms[o + 1] - rt.prevTransforms[o + 1]);
      const dz = Math.abs(rt.transforms[o + 2] - rt.prevTransforms[o + 2]);
      if (dx + dy + dz < SLEEP_MOVE_EPS) asleep = true;
    }
    if (asleep) candidates.push(rec);
  }
  candidates.sort((a, b) => a.dynamicOrder - b.dynamicOrder);

  let excess = rt.dynamicChunks - MAX_DYNAMIC_CHUNKS;
  for (const rec of candidates) {
    if (excess <= 0) break;
    rec.state = 'removed';
    rec.fade = 0;
    rt.removedChunks += 1;
    rt.dynamicChunks -= 1;
    excess -= 1;
  }
}

/**
 * Recount live body states and refresh bookkeeping cellCount.
 *
 * F4 identity (post-fracture, every sample):
 *   cellCount === staticCells + dynamicChunks + removedChunks
 * where removedChunks includes:
 *   - culled (240-cap fade eviction of asleep dynamics)
 *   - shard-degraded (dyn-cap at spawn or pure size-floor subdivide)
 * Intact wall: cellCount === pristineCellCount; static/dynamic/removed are 0.
 */
export function recountCells(rt: DemolitionRuntime) {
  let dyn = 0;
  let stat = 0;
  let fading = 0;
  let sub = 0;
  let minVol = Infinity;
  let maxVol = 0;
  for (const rec of rt.records.values()) {
    if (rec.state === 'dynamic') {
      dyn += 1;
      if (rec.isSubChunk) sub += 1;
      if (rec.volume < minVol) minVol = rec.volume;
      if (rec.volume > maxVol) maxVol = rec.volume;
    } else if (rec.state === 'static') {
      stat += 1;
    } else if (rec.state === 'removed' && rec.fade >= 0 && rec.fade < 1) {
      fading += 1;
    }
  }
  rt.dynamicChunks = dyn;
  rt.staticCells = stat;
  rt.fadingChunks = fading;
  rt.subChunks = sub;
  rt.minChunkVol = dyn > 0 && Number.isFinite(minVol) ? minVol : 0;
  rt.maxChunkVol = dyn > 0 ? maxVol : 0;

  if (!rt.fractured) {
    rt.baseCellCount = rt.pristineCellCount;
  } else {
    // Identity: every accounted piece is static, dynamic, or removed.
    rt.baseCellCount = stat + dyn + rt.removedChunks;
  }
}

export function computeMaxDynamicY(rt: DemolitionRuntime) {
  let maxY = 0;
  let any = false;
  for (const rec of rt.records.values()) {
    if (rec.state !== 'dynamic' || !rec.bodyId) continue;
    const idx = rt.bodyIndex.get(rec.bodyId);
    if (idx == null) continue;
    const y = rt.transforms[idx * 7 + 1];
    if (!any || y > maxY) {
      maxY = y;
      any = true;
    }
  }
  rt.maxDynamicY = any ? maxY : 0;
}

// ---------------------------------------------------------------------------
// Thrown demolition projectiles
// ---------------------------------------------------------------------------

export interface ProjectileSpec {
  radius: number;
  density: number;
  /** Optional calibrated damage density when rigid-body inertia differs. */
  damageDensity?: number;
  speed: number;
  friction: number;
  restitution: number;
}

const PROJECTILE_SPECS: Record<DemoTool, ProjectileSpec> = {
  cannon: {
    radius: CANNONBALL_RADIUS,
    density: CANNONBALL_PHYSICS_DENSITY,
    damageDensity: CANNONBALL_DENSITY,
    speed: CANNONBALL_SPEED,
    friction: 0.55,
    restitution: 0.02,
  },
  bomb: { radius: CHARGE_RADIUS, density: CHARGE_DENSITY, speed: CHARGE_SPEED, friction: 0.72, restitution: 0.02 },
  spike: { radius: SPIKE_RADIUS, density: SPIKE_DENSITY, speed: SPIKE_SPEED, friction: 0.5, restitution: 0.02 },
  rubble: { radius: RUBBLE_RADIUS, density: RUBBLE_DENSITY, speed: RUBBLE_SPEED, friction: 0.9, restitution: 0.08 },
  concussion: { radius: CONCUSSION_RADIUS, density: CONCUSSION_DENSITY, speed: CONCUSSION_SPEED, friction: 0.65, restitution: 0.04 },
};

export function spawnProjectile(
  rt: DemolitionRuntime,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
  weapon: DemoTool = 'cannon',
  powerOverride?: number,
) {
  const spec = PROJECTILE_SPECS[weapon] ?? PROJECTILE_SPECS.cannon;
  const power = clampWeaponPower(powerOverride ?? rt.weaponPower);
  const len = Math.hypot(dir.x, dir.y, dir.z) || 1;
  const ordinal = rt.nextProjectileOrdinal++;
  const bodyId = rt.cannonballPool.spawn(() => {
    const participant: DemolitionNativeParticipant = { kind: 'projectile', ordinal };
    const id = createOwnedBody(rt, {
      type: 'dynamic',
      position: [origin.x, origin.y, origin.z],
      ccd: true,
      // Rolling resistance: undamped spheres roll for tens of seconds and the
      // rest-retire dwell never triggers. Flight is short (<0.2s), so the
      // muzzle-speed loss from damping is ~2% — carve energy is unaffected
      // because fracture uses contact approachSpeed.
      linearDamping: 0.15,
      angularDamping: 0.5,
    }, participant);
    addOwnedSphere(rt, id, spec.radius, {
      density: spec.density,
      friction: spec.friction,
      restitution: spec.restitution,
    }, participant);
    rt.world.setLinearVelocity(id, [
      (dir.x / len) * spec.speed * power,
      (dir.y / len) * spec.speed * power,
      (dir.z / len) * spec.speed * power,
    ]);
    rt.projectileOrdinals.set(id as number, ordinal);
    return id;
  }, rt.stepper.simTime);
  rt.cannonballs.push(bodyId);
  rt.cannonballSet.add(bodyId);
  rt.projectileWeapons.set(bodyId, weapon);
  rt.projectilePowers.set(bodyId, power);
  rt.idsDirty = true;
}

export function spawnCannonball(
  rt: DemolitionRuntime,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
) {
  spawnProjectile(rt, origin, dir, 'cannon');
}

export function retireProjectile(rt: DemolitionRuntime, bodyId: number) {
  rt.cannonballPool.retire(bodyId as BodyHandle);
  const index = rt.cannonballs.indexOf(bodyId);
  if (index >= 0) rt.cannonballs.splice(index, 1);
  rt.cannonballSet.delete(bodyId);
  rt.projectileWeapons.delete(bodyId);
  rt.projectilePowers.delete(bodyId);
  rt.projectileRecoveryUntil.delete(bodyId);
  rt.projectileRestDwell.delete(bodyId);
  rt.projectileRecoveryCount.delete(bodyId);
  rt.embeddedProjectiles.delete(bodyId);
  rt.projectileGroundTraced.delete(bodyId);
  rt.projectileOrdinals.delete(bodyId);
  rt.idsDirty = true;
}

/**
 * Stick a spike into structure: zero velocity, kinematic if available, keep
 * the body visible until pool TTL or the anchor is carved away.
 */
export function embedSpikeProjectile(
  rt: DemolitionRuntime,
  ballId: number,
  point: { x: number; y: number; z: number },
) {
  try {
    rt.world.setLinearVelocity(ballId as BodyHandle, [0, 0, 0]);
  } catch {
    // Body may already be gone (pool race) — skip embed bookkeeping.
    return;
  }
  try {
    rt.world.setBodyType(ballId as BodyHandle, 'kinematic');
  } catch {
    // Body may already be gone (pool race) — skip embed bookkeeping.
    return;
  }
  rt.embeddedProjectiles.set(ballId, { x: point.x, y: point.y, z: point.z });
  rt.projectileRestDwell.delete(ballId);
  rt.projectileRecoveryUntil.delete(ballId);
}

/** Restore an embedded spike to free dynamic motion (falls with rubble). */
export function unembedSpikeProjectile(rt: DemolitionRuntime, ballId: number) {
  if (!rt.embeddedProjectiles.has(ballId)) return;
  rt.embeddedProjectiles.delete(ballId);
  try {
    rt.world.setBodyType(ballId as BodyHandle, 'dynamic');
  } catch {
    // Body may have been retired.
  }
  try {
    rt.world.wakeBody(ballId as BodyHandle);
  } catch {
    // ignore
  }
}

/**
 * Un-stick spikes whose embed point is near any just-released cell centroid.
 * O(embedded × released); both sets are tiny.
 */
export function unembedSpikesNearReleased(
  rt: DemolitionRuntime,
  released: Array<{ x: number; y: number; z: number }>,
) {
  if (rt.embeddedProjectiles.size === 0 || released.length === 0) return;
  const r2 = SPIKE_UNEMBED_RADIUS * SPIKE_UNEMBED_RADIUS;
  const toRelease: number[] = [];
  for (const [ballId, embed] of rt.embeddedProjectiles) {
    for (const p of released) {
      const dx = embed.x - p.x;
      const dy = embed.y - p.y;
      const dz = embed.z - p.z;
      if (dx * dx + dy * dy + dz * dz <= r2) {
        toRelease.push(ballId);
        break;
      }
    }
  }
  for (const ballId of toRelease) unembedSpikeProjectile(rt, ballId);
}

export function releasePositionsFromSamples(
  samples: Array<{ position: { x: number; y: number; z: number } }>,
): Array<{ x: number; y: number; z: number }> {
  return samples.map((s) => ({ x: s.position.x, y: s.position.y, z: s.position.z }));
}

export function queueProjectileRestPuff(
  rt: DemolitionRuntime,
  bodyId: number,
  position: { x: number; y: number; z: number },
) {
  let h = (Math.imul(bodyId | 0, 0x9e3779b1) ^ Math.imul(rt.wallVersion | 0, 0x85ebca6b)) >>> 0;
  for (let i = 0; i < 3; i += 1) {
    h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
    const angle = ((h ^ (h >>> 16)) >>> 0) / 0x1_0000_0000 * Math.PI * 2;
    const speed = 0.3 + i * 0.1;
    appendDemolitionEvent(rt, 'shard', {
      x: position.x + Math.cos(angle) * 0.02,
      y: position.y + 0.02,
      z: position.z + Math.sin(angle) * 0.02,
      vx: Math.cos(angle) * speed,
      vy: 0.25 + i * 0.08,
      vz: Math.sin(angle) * speed,
      volume: 0.0005,
      material: rt.profile.id,
    });
  }
}

// ---------------------------------------------------------------------------
// Contact → fracture
// ---------------------------------------------------------------------------

/**
 * Recover only a genuine projectile/surface overlap. Normal contacts are left
 * to Box3D: rewriting a ball's transform and velocity on every hit fights the
 * solver and becomes unstable once several balls and fragments share a crater.
 */
export function stabilizeProjectileContact(
  rt: DemolitionRuntime,
  ballId: number,
  otherId: number,
  weapon: DemoTool,
  hit: ContactHitEvent | undefined,
  fallbackSpeed: number,
) {
  // Embedded spikes are kinematic fixtures of the wall — never rewrite them.
  if (rt.embeddedProjectiles.has(ballId)) return;

  let nx = 0;
  let ny = 0;
  let nz = 0;
  if (hit) {
    // ContactHitEvent.normal points from bodyA toward bodyB. Convert it to
    // the outward direction for the projectile regardless of pair ordering.
    const sign = hit.bodyA === ballId ? -1 : 1;
    nx = hit.normal.x * sign;
    ny = hit.normal.y * sign;
    nz = hit.normal.z * sign;
  }

  const ballPos = bodyPositionLive(rt, ballId);
  if (Math.hypot(nx, ny, nz) < 1e-4) {
    const otherPos = bodyPositionLive(rt, otherId);
    if (ballPos && otherPos) {
      nx = ballPos.x - otherPos.x;
      ny = ballPos.y - otherPos.y;
      nz = ballPos.z - otherPos.z;
    }
  }

  let vx = 0;
  let vy = 0;
  let vz = 0;
  try {
    const v = rt.world.getLinearVelocity(ballId as BodyHandle);
    vx = v.x;
    vy = v.y;
    vz = v.z;
  } catch {
    // Use the contact normal fallback below.
  }

  const velocityLength = Math.hypot(vx, vy, vz);
  if (Math.hypot(nx, ny, nz) < 1e-4 && velocityLength > 1e-4) {
    // If no hard-contact normal is available, push opposite current travel.
    nx = -vx;
    ny = -vy;
    nz = -vz;
  }

  const length = Math.hypot(nx, ny, nz) || 1;
  nx /= length;
  ny /= length;
  nz /= length;

  const contactPoint = hit &&
    Number.isFinite(hit.point.x) &&
    Number.isFinite(hit.point.y) &&
    Number.isFinite(hit.point.z)
    ? hit.point
    : null;
  if (!ballPos || !contactPoint) return;

  const radius = PROJECTILE_SPECS[weapon]?.radius ?? CANNONBALL_RADIUS;
  const outwardDistance =
    (ballPos.x - contactPoint.x) * nx +
    (ballPos.y - contactPoint.y) * ny +
    (ballPos.z - contactPoint.z) * nz;
  // The solver has already separated the sphere unless its centre is still
  // inside its own radius on the outward normal.
  if (outwardDistance >= radius) return;

  const separation = Math.max(0.02, radius * 0.08);
  try {
    rt.world.setBodyTransform(
      ballId as BodyHandle,
      [
        contactPoint.x + nx * (radius + separation),
        contactPoint.y + ny * (radius + separation),
        contactPoint.z + nz * (radius + separation),
      ],
      [0, 0, 0, 1],
    );
  } catch {
    return;
  }
  rt.patchEngagements.stabilizeContact += 1;

  // Always provide a bounded outward release after a rubble contact. The old
  // code returned when the post-solver normal velocity was already near zero;
  // that left a ball separated but motionless against a wedged fragment.
  const normalVelocity = vx * nx + vy * ny + vz * nz;
  const tangentX = vx - normalVelocity * nx;
  const tangentY = vy - normalVelocity * ny;
  const tangentZ = vz - normalVelocity * nz;
  const releaseSpeed = Math.min(4.5, Math.max(1.25, fallbackSpeed * 0.07));
  try {
    rt.world.setLinearVelocity(ballId as BodyHandle, [
      tangentX * 0.45 + nx * releaseSpeed,
      tangentY * 0.45 + ny * releaseSpeed,
      tangentZ * 0.45 + nz * releaseSpeed,
    ]);
    rt.world.wakeBody(ballId as BodyHandle);
  } catch {
    // Ignore stale handles from a same-step pool retirement.
  }
}

/** Reset rubble sleep bookkeeping whenever a projectile disturbs a fragment. */
export function markRubbleActivity(rt: DemolitionRuntime, bodyId: number) {
  rt.lastActivitySimTime = rt.stepper.simTime;
  rt.sleepSlowStreak.delete(bodyId);
  const sampleIndex = rt.cellBodyIds.indexOf(bodyId);
  if (sampleIndex >= 0) {
    const offset = sampleIndex * 3;
    if (offset + 2 < rt.sleepSample.length) {
      rt.sleepSample[offset] = NaN;
      rt.sleepSample[offset + 1] = NaN;
      rt.sleepSample[offset + 2] = NaN;
    }
  }
  try {
    rt.world.wakeBody(bodyId as BodyHandle);
  } catch {
    // The fragment may have been removed during the same contact step.
  }
}

/**
 * Recover a projectile that has become motionless inside the wall/rubble
 * envelope. This is intentionally independent of impact thresholds: damage
 * needs a hard hit, but a trapped projectile needs a physical escape.
 */
export function recoverStalledProjectiles(rt: DemolitionRuntime) {
  const simTime = rt.stepper.simTime;
  for (const ballId of [...rt.cannonballs]) {
    // Stuck spikes must not be rescue-ejected or rest-dwell retired.
    if (rt.embeddedProjectiles.has(ballId)) continue;

    const position = bodyPositionLive(rt, ballId);
    if (!position) continue;

    // Kill-plane: a ball that sails past the ground slab (120 m) falls forever
    // and would only exit via the age TTL. Reap it as soon as it leaves the world.
    if (position.y < -2) {
      retireProjectile(rt, ballId);
      continue;
    }

    let velocity: { x: number; y: number; z: number };
    try {
      velocity = rt.world.getLinearVelocity(ballId as BodyHandle);
    } catch {
      continue;
    }
    const speed = Math.hypot(velocity.x, velocity.y, velocity.z);
    if (speed < PROJECTILE_REST_RESET_SPEED) {
      // Anything below flight speed is spent ordnance: resting, wedged, or
      // coasting. All of it accumulates toward retirement, and only genuine
      // flight resets the clock — every weapon launches at >=36 m/s, so a live
      // shot can never be reaped mid-trajectory. Accumulating on the whole band
      // matters: a ball coasting at ~1 m/s used to neither retire nor reset, so
      // it was force-woken every step and held `awake` above zero forever.
      const dwell = (rt.projectileRestDwell.get(ballId) ?? 0) + DEMO_FIXED_DT;
      rt.projectileRestDwell.set(ballId, dwell);
      if (dwell > PROJECTILE_REST_DWELL_SEC) {
        queueProjectileRestPuff(rt, ballId, position);
        retireProjectile(rt, ballId);
        continue;
      }
    } else {
      rt.projectileRestDwell.delete(ballId);
    }

    if (!rt.fractured || speed > PROJECTILE_STALL_SPEED_EPS) continue;

    const weapon = rt.projectileWeapons.get(ballId) ?? 'cannon';
    const radius = PROJECTILE_SPECS[weapon]?.radius ?? CANNONBALL_RADIUS;
    const margin = radius + PROJECTILE_WALL_CAGE_MARGIN;
    const inWallCage =
      Math.abs(position.x - rt.wallCenter.x) <= rt.wallHalf.x + margin &&
      position.y >= GROUND_EPS + radius * 0.5 &&
      position.y <= rt.wallFull.y + radius &&
      Math.abs(position.z - rt.wallCenter.z) <= rt.wallHalf.z + margin;
    if (!inWallCage) continue;

    const recoverAfter = rt.projectileRecoveryUntil.get(ballId) ?? 0;
    if (simTime < recoverAfter) continue;

    // A ball that needs repeated recoveries is wedged in the crater; re-ejecting
    // it forever resets its rest dwell and keeps the rubble island awake. Spend
    // the recovery budget, then retire it as spent ordnance.
    const recoveries = rt.projectileRecoveryCount.get(ballId) ?? 0;
    if (recoveries >= PROJECTILE_MAX_RECOVERIES) {
      queueProjectileRestPuff(rt, ballId, position);
      retireProjectile(rt, ballId);
      continue;
    }
    rt.projectileRecoveryCount.set(ballId, recoveries + 1);

    ejectProjectileFromWallFace(rt, ballId, weapon, position, Math.max(20, CANNONBALL_SPEED * 0.1));
    rt.patchEngagements.stallRecover += 1;
    rt.projectileRecoveryUntil.set(ballId, simTime + PROJECTILE_STALL_RECOVERY_COOLDOWN);
  }
}

/**
 * A static wall cell can be destroyed by the same contact that stopped the
 * projectile. The solver has no surviving shape from which to produce a clean
 * separation, so eject from the *wall face* rather than an arbitrary convex
 * fragment face. This is intentionally not used for loose rubble contacts.
 */
export function ejectProjectileFromWallFace(
  rt: DemolitionRuntime,
  ballId: number,
  weapon: DemoTool,
  point: { x: number; y: number; z: number },
  approachSpeed: number,
) {
  const local = {
    x: (point.x - rt.wallCenter.x) / Math.max(rt.wallHalf.x, 1e-4),
    y: (point.y - rt.wallCenter.y) / Math.max(rt.wallHalf.y, 1e-4),
    z: (point.z - rt.wallCenter.z) / Math.max(rt.wallHalf.z, 1e-4),
  };
  const ax = Math.abs(local.x);
  const ay = Math.abs(local.y);
  const az = Math.abs(local.z);
  let nx = 0;
  let ny = 0;
  let nz = 0;
  if (ax >= ay && ax >= az) nx = local.x >= 0 ? 1 : -1;
  else if (ay >= az) ny = local.y >= 0 ? 1 : -1;
  else nz = local.z >= 0 ? 1 : -1;

  const radius = PROJECTILE_SPECS[weapon]?.radius ?? CANNONBALL_RADIUS;
  const separation = Math.max(0.025, radius * 0.1);
  try {
    rt.world.setBodyTransform(
      ballId as BodyHandle,
      [
        point.x + nx * (radius + separation),
        point.y + ny * (radius + separation),
        point.z + nz * (radius + separation),
      ],
      [0, 0, 0, 1],
    );

    const previous = rt.world.getLinearVelocity(ballId as BodyHandle);
    const normalSpeed = previous.x * nx + previous.y * ny + previous.z * nz;
    const tangentX = previous.x - normalSpeed * nx;
    const tangentY = previous.y - normalSpeed * ny;
    const tangentZ = previous.z - normalSpeed * nz;
    const exitSpeed = Math.min(5, Math.max(1.5, approachSpeed * 0.07));
    rt.world.setLinearVelocity(ballId as BodyHandle, [
      tangentX * 0.15 + nx * exitSpeed,
      tangentY * 0.15 + ny * exitSpeed,
      tangentZ * 0.15 + nz * exitSpeed,
    ]);
    rt.world.wakeBody(ballId as BodyHandle);
    rt.patchEngagements.wallFaceEject += 1;
  } catch {
    // A fragment can be removed in this same step; keep the solver result.
  }
}

/**
 * Apply the missing counterpart of a projectile/rubble collision. Box3D has
 * already resolved penetration, but its small restitution makes a heavy chunk
 * look frozen after a high-speed hit. We add the bounded normal velocity from
 * the two-body collision equation and wake the actual fragment.
 */
export function kickDynamicRubble(
  rt: DemolitionRuntime,
  bodyId: number,
  projectileToOther: { x: number; y: number; z: number },
  approachSpeed: number,
  projectileMass: number,
): number {
  const target = [...rt.records.values()].find(
    (rec) => rec.bodyId === bodyId && rec.state === 'dynamic',
  );
  if (!target) return 0;

  const directionLength = Math.hypot(
    projectileToOther.x,
    projectileToOther.y,
    projectileToOther.z,
  );
  if (directionLength < 1e-5) return 0;
  const nx = projectileToOther.x / directionLength;
  const ny = projectileToOther.y / directionLength;
  const nz = projectileToOther.z / directionLength;

  let current = { x: 0, y: 0, z: 0 };
  try {
    current = rt.world.getLinearVelocity(bodyId as BodyHandle);
  } catch {
    // A stale fragment can be removed by the same simulation step.
    return 0;
  }

  const currentNormalSpeed = current.x * nx + current.y * ny + current.z * nz;
  const targetMass = Math.max(0.05, target.mass);
  const targetNormalSpeed = Math.min(
    3,
    ((1 + RUBBLE_CONTACT_RESTITUTION) * projectileMass * approachSpeed) /
      (projectileMass + targetMass),
  );
  const addedNormalSpeed = Math.min(3, Math.max(0, targetNormalSpeed - currentNormalSpeed));
  // Return the chunk's resulting outbound normal speed, not the added deficit:
  // when the solver already moved the chunk, no top-up is applied but the
  // contact still visibly kicked it — telemetry must reflect that.
  if (addedNormalSpeed < 0.01) return Math.max(0, currentNormalSpeed);

  applyImpulseToBody(
    rt,
    bodyId as BodyHandle,
    {
      vx: current.x + nx * addedNormalSpeed,
      vy: current.y + ny * addedNormalSpeed,
      vz: current.z + nz * addedNormalSpeed,
    },
  );
  rt.patchEngagements.rubbleKick += 1;
  return Math.max(0, currentNormalSpeed + addedNormalSpeed);
}

export function bodyPosition(
  rt: DemolitionRuntime,
  bodyId: number,
): { x: number; y: number; z: number } | null {
  const idx = rt.bodyIndex.get(bodyId);
  if (idx == null) return null;
  const o = idx * 7;
  return { x: rt.transforms[o], y: rt.transforms[o + 1], z: rt.transforms[o + 2] };
}

/** Fresh single-body pose from the world (avoids stale buffer zeros). */
export function bodyPositionLive(
  rt: DemolitionRuntime,
  bodyId: number,
): { x: number; y: number; z: number } | null {
  try {
    rt.posScratchIds[0] = bodyId;
    rt.world.readTransforms(rt.posScratchIds, rt.posScratchOut);
    const x = rt.posScratchOut[0];
    const y = rt.posScratchOut[1];
    const z = rt.posScratchOut[2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
    return { x, y, z };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Buffers / advance
// ---------------------------------------------------------------------------

export function rebuildBuffers(rt: DemolitionRuntime) {
  const ids: number[] = [];
  // Insertion order: cells (by cell id order for stability), then cannonballs.
  const sorted = Array.from(rt.records.values())
    .filter((r) => r.bodyId && r.state !== 'removed')
    .sort((a, b) => a.cellId - b.cellId);
  for (const rec of sorted) {
    rec.bufferIndex = ids.length;
    ids.push(rec.bodyId);
  }
  // Monolith (intact only)
  if (!rt.fractured && rt.monolithBodyId) {
    ids.push(rt.monolithBodyId);
  }
  for (const ball of rt.cannonballs) ids.push(ball);

  rt.buffer = new TransformBuffer(Math.max(ids.length, 1));
  for (const id of ids) rt.buffer.add(id as BodyHandle);
  rt.buffer.rebuild();

  rt.bodyIds = rt.buffer.ids;
  rt.transforms = rt.buffer.transforms;
  rt.prevTransforms = new Float32Array(ids.length * 7);
  rt.prevValid = false;
  rt.bodyIndex = new Map(ids.map((id, i) => [id, i]));

  // Dynamic cell bodies for sleep-state probes / rubble cull (NOT SleepManager.watch —
  // watch() re-arms force-sleep of the entire set on every rebuild).
  const dynamicIds: number[] = [];
  for (const rec of rt.records.values()) {
    if (rec.state === 'dynamic' && rec.bodyId) dynamicIds.push(rec.bodyId);
  }
  rt.cellBodyIds = Int32Array.from(dynamicIds);
  rt.cellSleepStates = new Uint8Array(dynamicIds.length);
  // Drop streaks for bodies no longer dynamic.
  for (const id of rt.sleepSlowStreak.keys()) {
    if (!dynamicIds.includes(id)) rt.sleepSlowStreak.delete(id);
  }
  // Invalidate displacement baseline when the tracked set changes size so we
  // never compare against a sample from a different body layout.
  if (rt.sleepSample.length !== dynamicIds.length * 3) {
    rt.sleepSample = new Float32Array(dynamicIds.length * 3);
    rt.sleepSampleValid = false;
  }
  rt.idsDirty = false;
}

/**
 * Settled-body sleep without mid-air freezes.
 *
 * - Never force-sleeps (forceSleepSettled froze airborne collapse rubble).
 * - Displacement <1cm over the full ~2s sweep interval → sleep.
 * - Else |v| < SLEEP_VEL_EPS near ground → sleep residual micro-jitter and
 *   endless rolling rubble.
 * - Free-falling chunks always exceed 1cm over 2s under gravity, so they
 *   cannot pass the displacement test mid-air.
 * - Tracks cell rubble only. Projectiles remain transient and are rest-retired
 *   or age-evicted instead of entering the rubble sleep policy.
 */
export function demolitionSleepSweep(rt: DemolitionRuntime) {
  // Build watch list from structural rubble only. Projectiles are transient
  // actors and must never be put to sleep by the rubble settle policy.
  const watchIds: number[] = [];
  for (let i = 0; i < rt.cellBodyIds.length; i += 1) {
    watchIds.push(rt.cellBodyIds[i]);
  }

  const n = watchIds.length;
  if (n === 0) {
    rt.sleepSampleValid = false;
    return;
  }

  const simT = rt.stepper.simTime;
  const moveEpsSq = SLEEP_MOVE_EPS * SLEEP_MOVE_EPS;

  if (!rt.sleepSampleValid || rt.sleepSample.length < n * 3) {
    rt.sleepSample = new Float32Array(n * 3);
    for (let i = 0; i < n; i += 1) {
      const bodyId = watchIds[i];
      const idx = rt.bodyIndex.get(bodyId);
      const s = i * 3;
      if (idx == null) {
        rt.sleepSample[s] = NaN;
        rt.sleepSample[s + 1] = NaN;
        rt.sleepSample[s + 2] = NaN;
        continue;
      }
      const o = idx * 7;
      rt.sleepSample[s] = rt.transforms[o];
      rt.sleepSample[s + 1] = rt.transforms[o + 1];
      rt.sleepSample[s + 2] = rt.transforms[o + 2];
    }
    rt.sleepSampleValid = true;
    rt.nextSleepSweep = simT + SLEEP_SWEEP_INTERVAL;
    return;
  }

  if (simT < rt.nextSleepSweep) return;

  for (let i = 0; i < n; i += 1) {
    const bodyId = watchIds[i];
    const idx = rt.bodyIndex.get(bodyId);
    const s = i * 3;
    if (idx == null) {
      rt.sleepSlowStreak.set(bodyId, 0);
      continue;
    }
    const o = idx * 7;
    const x = rt.transforms[o];
    const y = rt.transforms[o + 1];
    const z = rt.transforms[o + 2];
    if (
      !Number.isFinite(rt.sleepSample[s]) ||
      !Number.isFinite(rt.sleepSample[s + 1]) ||
      !Number.isFinite(rt.sleepSample[s + 2])
    ) {
      rt.sleepSample[s] = x;
      rt.sleepSample[s + 1] = y;
      rt.sleepSample[s + 2] = z;
      rt.sleepSlowStreak.delete(bodyId);
      continue;
    }
    const dx = x - rt.sleepSample[s];
    const dy = y - rt.sleepSample[s + 1];
    const dz = z - rt.sleepSample[s + 2];
    const distSq = dx * dx + dy * dy + dz * dz;

    let lowEnergy = false;
    try {
      const v = rt.world.getLinearVelocity(bodyId as BodyHandle);
      const speed = Math.hypot(v.x, v.y, v.z);
      lowEnergy = speed < SLEEP_VEL_EPS;
    } catch {
      lowEnergy = false;
    }

    // Displacement <1cm over the full interval → sleep (includes micro-jitter
    // piles). Low-energy path is ONLY for near-ground residual rollers that
    // still exceed 1cm over 2s — never sleep airborne bodies via velocity
    // alone (apex of a free-fall arc can have |v|≈0 while still high).
    //
    const nearGround = y < 1.5;
    const moveOk = distSq < moveEpsSq;
    // Displacement alone is a valid settle signal at ANY height: a body that
    // moved <1cm across a full sweep interval is resting on something (free
    // fall covers ~19m in 2s) — chunks wedged in the hole rim or perched on
    // the remnant are legitimately at rest and must sleep, or awake never
    // returns to 0 for piles/ledges above the near-ground band.
    const settled = moveOk || (nearGround && lowEnergy);
    if (settled) {
      const streak = (rt.sleepSlowStreak.get(bodyId) ?? 0) + 1;
      rt.sleepSlowStreak.set(bodyId, streak);
      if (streak >= SLEEP_SLOW_STREAK) {
        let speed = 0;
        try {
          const v = rt.world.getLinearVelocity(bodyId as BodyHandle);
          speed = Math.hypot(v.x, v.y, v.z);
        } catch {
          speed = 0;
        }
        // Supported rest is structural (contact points), not "hasn't moved" —
        // a mid-air freeze also has zero displacement.
        let contactPoints: number | undefined;
        try {
          const load = rt.world.getBodyContactLoad(bodyId as BodyHandle);
          contactPoints = load.pointCount;
        } catch {
          contactPoints = undefined;
        }
        assertSleepAllowed(rt.invariantMon, {
          bodyId,
          y,
          speed,
          displacementOk: moveOk,
          nearGround,
          simTime: simT,
          contactPoints,
        });
        try {
          rt.world.sleepBody(bodyId as BodyHandle);
        } catch {
          // ignore
        }
      }
    } else {
      rt.sleepSlowStreak.set(bodyId, 0);
    }

    rt.sleepSample[s] = x;
    rt.sleepSample[s + 1] = y;
    rt.sleepSample[s + 2] = z;
  }

  rt.nextSleepSweep = simT + SLEEP_SWEEP_INTERVAL;
}

export function wakeDemolitionProjectiles(rt: DemolitionRuntime): void {
  for (const ballId of rt.cannonballs) {
    if (rt.embeddedProjectiles.has(ballId)) continue;
    let moving = false;
    try {
      const velocity = rt.world.getLinearVelocity(ballId as BodyHandle);
      moving = Math.hypot(velocity.x, velocity.y, velocity.z) > 0.25;
    } catch { continue; }
    if (!moving) continue;
    try { rt.world.wakeBody(ballId as BodyHandle); } catch { /* retired */ }
  }
}
